import type { DashboardAlert, DashboardMetrics } from "@fiscalo/shared-types";

export const BAND_HEALTHY = 80;
export const BAND_NEEDS_ATTENTION = 50;

export type HealthBand = "healthy" | "needsAttention" | "atRisk";

export interface HealthScoreBreakdown {
  score: number;
  band: HealthBand;
  penalties: {
    overdue: number;
    dueIn7Days: number;
    noCae: number;
    caeLow: number;
    noInvoicesThisMonth: number;
  };
  /** When present, breakdown comes from GET /fiscal-health (obligation-based). */
  fiscalApiFactors?: {
    overdueCount: number;
    dueIn3Count: number;
    missingBpsNumber: boolean;
    currentMonthComplete: boolean;
    pendingAckOverdueCount: number;
    pendingAckDueSoonCount: number;
    pendingAckFreshCount: number;
  };
}

/**
 * NOTE — there is deliberately no client-side score function here.
 *
 * One used to exist (computeHealthScore) as a fallback for
 * GET /fiscal-health. It could not work: it charged 25 points per overdue
 * obligation taken from getObligationCounts(), which GENERATED obligations
 * from the fiscal calendar and had no knowledge of what the tenant had filed,
 * so every past deadline counted as overdue. A flawless tenant scored 0/100
 * "Atención requerida" on every dashboard load until the request resolved.
 *
 * The portal has no filing state, so the number can only come from the
 * backend, which reads persisted obligations and their status. Callers render
 * a loading or error state instead of substituting a number of their own.
 */

export function getHealthBandLabelKey(band: HealthBand): string {
  const keyMap: Record<HealthBand, string> = {
    healthy: "dashboard.headerSummary.healthy",
    needsAttention: "dashboard.headerSummary.needsAttention",
    atRisk: "dashboard.headerSummary.atRisk",
  };
  return keyMap[band];
}

/** Backend formula: 20 per overdue, 10 per due today/in 1/in 3. Use so displayed score matches modal. */
const OBLIGATION_OVERDUE_PENALTY = 20;
const OBLIGATION_DUE_SOON_PENALTY = 10;

export function scoreFromObligationCounts(
  overdueCount: number,
  dueTodayCount: number,
  dueIn1Count: number,
  dueIn3Count: number
): number {
  const dueSoonCount = dueTodayCount + dueIn1Count + dueIn3Count;
  const score =
    100 -
    overdueCount * OBLIGATION_OVERDUE_PENALTY -
    dueSoonCount * OBLIGATION_DUE_SOON_PENALTY;
  return Math.max(0, Math.min(100, score));
}

/**
 * When the local agenda has more obligation penalties than the upstream score
 * already accounts for, derive the score from the agenda so the displayed
 * number matches the modal's per-obligation breakdown. Caller passes the
 * counts of obligations they consider penalizing (often filtered to a single
 * authority — e.g. DGI-only for the dashboard's operation-status card).
 *
 * Crucially we take Math.min(breakdown.score, agendaScore) — the API score
 * incorporates penalties (BPS, returned filings, etc.) that the local
 * filtered agenda doesn't see. Replacing wholesale used to bump a 0/100 API
 * score up to 80 because the dashboard's DGI-only filter saw "just one IVA
 * overdue" and the operation-status card then claimed "Todo en regla con
 * DGI" while the hero on the same screen showed Atención requerida.
 */
export function effectiveHealthBreakdown(
  breakdown: HealthScoreBreakdown,
  overdueCount: number,
  dueTodayCount: number,
  dueIn1Count: number,
  dueIn3Count: number
): HealthScoreBreakdown {
  const hasAgendaPenalties =
    overdueCount > 0 || dueTodayCount > 0 || dueIn1Count > 0 || dueIn3Count > 0;
  if (!hasAgendaPenalties) return breakdown;
  const agendaScore = scoreFromObligationCounts(
    overdueCount,
    dueTodayCount,
    dueIn1Count,
    dueIn3Count
  );
  const score = Math.min(breakdown.score, agendaScore);
  const band: HealthBand =
    score >= BAND_HEALTHY ? "healthy" : score >= BAND_NEEDS_ATTENTION ? "needsAttention" : "atRisk";
  return {
    ...breakdown,
    score,
    band,
    penalties: {
      ...breakdown.penalties,
      overdue: Math.max(breakdown.penalties.overdue, overdueCount),
      dueIn7Days: Math.max(
        breakdown.penalties.dueIn7Days,
        dueTodayCount + dueIn1Count + dueIn3Count,
      ),
    },
    fiscalApiFactors: breakdown.fiscalApiFactors
      ? {
          ...breakdown.fiscalApiFactors,
          overdueCount: Math.max(
            breakdown.fiscalApiFactors.overdueCount,
            overdueCount,
          ),
          dueIn3Count: Math.max(
            breakdown.fiscalApiFactors.dueIn3Count,
            dueTodayCount + dueIn1Count + dueIn3Count,
          ),
        }
      : undefined,
  };
}
